import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Check, Mail, Phone, Download, Calendar, FileText,
  HelpCircle, Plus, Trash2, GripVertical, Zap, Upload, Image, ChevronDown
} from 'lucide-react';
import uploadService from '../../../services/uploadService';

const LEAD_CAPTURE_TYPES = [
  {
    id: 'email_capture',
    label: 'Email Capture',
    icon: Mail,
    description: 'Simple email collection',
    color: 'from-blue-500 to-blue-600',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500',
    iconColor: 'text-blue-400',
    defaultFields: [
      { id: '1', name: 'email', label: 'Email Address', type: 'email', required: true, placeholder: 'you@example.com', displayOrder: 0 }
    ],
    defaultHeadline: 'Stay Updated',
    defaultSubheadline: 'Subscribe to our newsletter for the latest updates',
    defaultButtonText: 'Subscribe'
  },
  {
    id: 'sms_optin',
    label: 'SMS Opt-in',
    icon: Phone,
    description: 'Phone number with SMS consent',
    color: 'from-green-500 to-green-600',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500',
    iconColor: 'text-green-400',
    defaultFields: [
      { id: '1', name: 'name', label: 'Full Name', type: 'text', required: true, placeholder: 'John Doe', displayOrder: 0 },
      { id: '2', name: 'phone', label: 'Phone Number', type: 'phone', required: true, placeholder: '+1 (555) 123-4567', displayOrder: 1 }
    ],
    defaultHeadline: 'Get SMS Updates',
    defaultSubheadline: 'Receive exclusive offers and updates via text message',
    defaultButtonText: 'Opt In',
    requiresConsent: true,
    defaultConsentText: 'I agree to receive SMS messages. Msg & data rates may apply.'
  },
  {
    id: 'lead_magnet',
    label: 'Lead Magnet',
    icon: Download,
    description: 'Content gated download',
    color: 'from-purple-500 to-purple-600',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500',
    iconColor: 'text-purple-400',
    defaultFields: [
      { id: '1', name: 'name', label: 'Full Name', type: 'text', required: true, placeholder: 'John Doe', displayOrder: 0 },
      { id: '2', name: 'email', label: 'Email Address', type: 'email', required: true, placeholder: 'you@example.com', displayOrder: 1 }
    ],
    defaultHeadline: 'Get Your Free Guide',
    defaultSubheadline: 'Download our exclusive guide instantly',
    defaultButtonText: 'Download Now'
  },
  {
    id: 'replay_upsell',
    label: 'Replay Access',
    icon: FileText,
    description: 'Webinar replay signup',
    color: 'from-orange-500 to-orange-600',
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-orange-500',
    iconColor: 'text-orange-400',
    defaultFields: [
      { id: '1', name: 'name', label: 'Full Name', type: 'text', required: true, placeholder: 'John Doe', displayOrder: 0 },
      { id: '2', name: 'email', label: 'Email Address', type: 'email', required: true, placeholder: 'you@example.com', displayOrder: 1 }
    ],
    defaultHeadline: 'Watch the Replay',
    defaultSubheadline: 'Get instant access to the full recording',
    defaultButtonText: 'Send Me the Replay'
  },
  {
    id: 'calendar_booking',
    label: 'Calendar Booking',
    icon: Calendar,
    description: 'Schedule a call',
    color: 'from-cyan-500 to-cyan-600',
    bgColor: 'bg-cyan-500/10',
    borderColor: 'border-cyan-500',
    iconColor: 'text-cyan-400',
    defaultFields: [
      { id: '1', name: 'name', label: 'Full Name', type: 'text', required: true, placeholder: 'John Doe', displayOrder: 0 },
      { id: '2', name: 'email', label: 'Email Address', type: 'email', required: true, placeholder: 'you@example.com', displayOrder: 1 },
      { id: '3', name: 'company', label: 'Company', type: 'text', required: false, placeholder: 'Your company', displayOrder: 2 }
    ],
    defaultHeadline: 'Book a Call',
    defaultSubheadline: 'Schedule a time that works for you',
    defaultButtonText: 'Book Now'
  },
  {
    id: 'quiz_assessment',
    label: 'Quiz Assessment',
    icon: HelpCircle,
    description: 'Interactive quiz',
    color: 'from-pink-500 to-pink-600',
    bgColor: 'bg-pink-500/10',
    borderColor: 'border-pink-500',
    iconColor: 'text-pink-400',
    defaultFields: [
      { id: '1', name: 'name', label: 'Your Name', type: 'text', required: true, placeholder: 'John Doe', displayOrder: 0 },
      { id: '2', name: 'email', label: 'Email Address', type: 'email', required: true, placeholder: 'you@example.com', displayOrder: 1 }
    ],
    defaultHeadline: 'Take the Quiz',
    defaultSubheadline: 'Discover your results in just 2 minutes',
    defaultButtonText: 'Start Quiz'
  },
];

const FIELD_TYPES = [
  { id: 'email', label: 'Email' },
  { id: 'text', label: 'Text' },
  { id: 'phone', label: 'Phone' },
  { id: 'textarea', label: 'Long Text' },
  { id: 'select', label: 'Dropdown' },
  { id: 'checkbox', label: 'Checkbox' },
  { id: 'number', label: 'Number' },
  { id: 'url', label: 'URL' },
];

const CRM_PROVIDERS = [
  { id: 'webhook', label: 'Webhook (Zapier/Make)' },
  { id: 'gohighlevel', label: 'GoHighLevel' },
  { id: 'hubspot', label: 'HubSpot' },
  { id: 'mailchimp', label: 'Mailchimp' },
  { id: 'activecampaign', label: 'ActiveCampaign' },
];

const DEFAULT_FORM_DATA = {
  name: '',
  type: 'email_capture',
  formConfig: {
    headline: 'Get Instant Access',
    subheadline: 'Enter your email to receive the free guide',
    buttonText: 'Get Access',
    fields: [
      { id: '1', name: 'email', label: 'Email Address', type: 'email', required: true, placeholder: 'you@example.com', displayOrder: 0 }
    ],
    downloadUrl: '',
    calendarUrl: '',
  },
  consent: {
    enabled: false,
    text: 'I agree to receive marketing emails',
    required: true,
    privacyPolicyUrl: ''
  },
  crmIntegration: {
    enabled: false,
    provider: 'webhook',
    webhookUrl: ''
  },
  branding: {
    primaryColor: '#8B5CF6',
    buttonColor: '#8B5CF6',
    backgroundColor: '#1F2937',
    logoUrl: '',
    imageUrl: ''
  },
  submitBehavior: {
    successMessage: 'Thanks! Check your email for the download link.',
    showInlineMessage: true,
    closeFormOnSubmit: false,
    downloadTrigger: false,
    redirectUrl: '',
  }
};

// Custom Select Component - uses portal to avoid overflow clipping
const Select = ({ value, onChange, options, placeholder, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({});

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        ref.current && !ref.current.contains(e.target) &&
        (!dropdownRef.current || !dropdownRef.current.contains(e.target))
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen || !ref.current) return;
    const updatePosition = () => {
      const rect = ref.current.getBoundingClientRect();
      const dropdownHeight = 240;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < dropdownHeight && rect.top > spaceBelow;
      setDropdownStyle({
        position: 'fixed', left: rect.left, width: rect.width, zIndex: 99999,
        ...(openUpward ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
      });
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => { window.removeEventListener('scroll', updatePosition, true); window.removeEventListener('resize', updatePosition); };
  }, [isOpen]);

  const selectedOption = options.find(opt => opt.id === value);

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm hover:bg-white/10 transition-colors"
      >
        <span>{selectedOption?.label || selectedOption?.name || placeholder}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && ReactDOM.createPortal(
        <div ref={dropdownRef} style={dropdownStyle} className="bg-[#1a1a2e] border border-white/10 rounded-lg shadow-xl max-h-60 overflow-y-auto">
          {options.map((option) => (
            <button
              key={option.id}
              onClick={() => { onChange(option.id); setIsOpen(false); }}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-white/10 transition-colors ${
                option.id === value ? 'text-emerald-400 bg-white/5' : 'text-gray-300'
              }`}
            >
              {option.label || option.name}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

// Custom Switch Component
const Switch = ({ checked, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={!!checked}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
      checked ? 'bg-[#6a1fbf]' : 'bg-white/20'
    }`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${
        checked ? 'translate-x-5' : 'translate-x-0.5'
      }`}
    />
  </button>
);

// Custom Tabs Component
const Tabs = ({ value, onChange, tabs }) => (
  <div className="flex bg-white/5 rounded-lg p-1">
    {tabs.map((tab) => (
      <button
        key={tab.id}
        onClick={() => onChange(tab.id)}
        className={`flex-1 text-xs py-2 px-3 rounded-md transition-colors ${
          value === tab.id
            ? 'bg-white/10 text-white'
            : 'text-gray-400 hover:text-white'
        }`}
      >
        {tab.label}
      </button>
    ))}
  </div>
);

export default function LeadCaptureModal({
  isOpen,
  onClose,
  onSave,
  leadCapture,
  folders,
  currentFolder,
  skipFolderSelect = false,
  readOnly = false,
  onCopyToMyElements
}) {
  const [activeTab, setActiveTab] = useState('type');
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [folderId, setFolderId] = useState(currentFolder?.id || '');
  const [uploading, setUploading] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Initialize form data only when modal opens, not on every prop change
  useEffect(() => {
    if (isOpen && !hasInitialized) {
      if (leadCapture) {
        setFormData({
          ...DEFAULT_FORM_DATA,
          ...leadCapture,
          formConfig: { ...DEFAULT_FORM_DATA.formConfig, ...leadCapture.formConfig },
          consent: { ...DEFAULT_FORM_DATA.consent, ...leadCapture.consent },
          crmIntegration: { ...DEFAULT_FORM_DATA.crmIntegration, ...leadCapture.crmIntegration },
          branding: { ...DEFAULT_FORM_DATA.branding, ...leadCapture.branding },
          submitBehavior: { ...DEFAULT_FORM_DATA.submitBehavior, ...leadCapture.submitBehavior }
        });
        setActiveTab('form');
      } else {
        setFormData(DEFAULT_FORM_DATA);
        setActiveTab('type');
      }
      setFolderId(currentFolder?.id || '');
      setHasInitialized(true);
    }
  }, [isOpen, leadCapture, currentFolder, hasInitialized]);

  // Reset initialization flag when modal closes
  useEffect(() => {
    if (!isOpen) {
      setHasInitialized(false);
    }
  }, [isOpen]);

  const updateFormData = (path, value) => {
    setFormData(prev => {
      const parts = path.split('.');
      const newData = { ...prev };
      let current = newData;

      for (let i = 0; i < parts.length - 1; i++) {
        current[parts[i]] = { ...current[parts[i]] };
        current = current[parts[i]];
      }
      current[parts[parts.length - 1]] = value;

      return newData;
    });
  };

  // Handle type change with type-specific defaults
  const handleTypeChange = (typeId) => {
    const typeConfig = LEAD_CAPTURE_TYPES.find(t => t.id === typeId);
    if (!typeConfig) return;

    setFormData(prev => ({
      ...prev,
      type: typeId,
      formConfig: {
        ...prev.formConfig,
        headline: typeConfig.defaultHeadline || prev.formConfig.headline,
        subheadline: typeConfig.defaultSubheadline || prev.formConfig.subheadline,
        buttonText: typeConfig.defaultButtonText || prev.formConfig.buttonText,
        fields: typeConfig.defaultFields || prev.formConfig.fields,
      },
      consent: {
        ...prev.consent,
        enabled: typeConfig.requiresConsent || prev.consent.enabled,
        text: typeConfig.defaultConsentText || prev.consent.text,
      }
    }));
  };

  const addField = () => {
    const newField = {
      id: Date.now().toString(),
      name: `field_${formData.formConfig.fields.length + 1}`,
      label: 'New Field',
      type: 'text',
      required: false,
      placeholder: '',
      displayOrder: formData.formConfig.fields.length
    };
    updateFormData('formConfig.fields', [...formData.formConfig.fields, newField]);
  };

  const updateField = (fieldId, updates) => {
    const fields = formData.formConfig.fields.map(f =>
      f.id === fieldId ? { ...f, ...updates } : f
    );
    updateFormData('formConfig.fields', fields);
  };

  const removeField = (fieldId) => {
    const fields = formData.formConfig.fields.filter(f => f.id !== fieldId);
    updateFormData('formConfig.fields', fields);
  };

  const handleSave = () => {
    onSave({
      ...formData,
      folder_id: folderId
    });
    onClose();
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await uploadService.uploadFile(file, 'lead-capture');
      updateFormData(`branding.${field}`, result.url);
    } catch (err) {
      console.error('Failed to upload:', err);
      alert('Failed to upload. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const isValid = formData.name.trim() && formData.formConfig.fields.length > 0 && (skipFolderSelect || folderId);

  if (!isOpen) return null;

  const tabs = [
    { id: 'type', label: 'Type' },
    { id: 'form', label: 'Form' },
    { id: 'branding', label: 'Branding' },
    { id: 'integration', label: 'Integration' },
    { id: 'preview', label: 'Preview' },
  ];

  const modalContent = (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
        style={{ zIndex: 9999 }}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-[#001233] border border-white/10 rounded-2xl w-full max-w-4xl h-[85vh] flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              {(() => {
                const selectedType = LEAD_CAPTURE_TYPES.find(t => t.id === formData.type);
                const Icon = selectedType?.icon || Mail;
                return (
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${selectedType?.color || 'from-emerald-500 to-teal-600'} flex items-center justify-center`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                );
              })()}
              <div>
                <h2 className="text-lg font-bold text-white">
                  {leadCapture ? 'Edit Lead Capture' : 'Create Lead Capture'}
                </h2>
                <p className="text-xs text-gray-400">
                  {LEAD_CAPTURE_TYPES.find(t => t.id === formData.type)?.label || 'Capture leads with customizable forms'}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Tabs */}
          <div className="px-4 pt-4">
            <Tabs value={activeTab} onChange={setActiveTab} tabs={tabs} />
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {readOnly && (
              <div className="flex items-start gap-3 mb-4 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                <p className="text-sm text-purple-200">
                  This element is from a shared library and is <strong>view only</strong>. Copy it to your library to make changes.
                </p>
              </div>
            )}
            {/* Type Selection */}
            {activeTab === 'type' && (
              <div className="space-y-4">
                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Form Name *</label>
                  <input
                    value={formData.name}
                    onChange={(e) => updateFormData('name', e.target.value)}
                    placeholder="e.g., Webinar Lead Magnet"
                    disabled={readOnly}
                    className={`w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 ${readOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
                  />
                </div>

                {!skipFolderSelect && folders?.length > 0 && (
                  <div>
                    <label className="text-gray-400 text-sm mb-2 block">Folder *</label>
                    <Select
                      value={folderId}
                      onChange={setFolderId}
                      options={folders.map(f => ({ id: f.id, name: f.name }))}
                      placeholder="Select folder..."
                    />
                  </div>
                )}

                <div>
                  <label className="text-gray-400 text-sm mb-3 block">Lead Capture Type</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {LEAD_CAPTURE_TYPES.map(type => {
                      const Icon = type.icon;
                      const isSelected = formData.type === type.id;
                      return (
                        <button
                          key={type.id}
                          onClick={() => handleTypeChange(type.id)}
                          className={`p-4 rounded-xl border text-left transition-all ${
                            isSelected
                              ? `${type.borderColor} ${type.bgColor}`
                              : 'border-white/10 hover:border-white/20 bg-white/5'
                          }`}
                        >
                          <div className={`w-10 h-10 rounded-lg mb-3 flex items-center justify-center bg-gradient-to-br ${type.color}`}>
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          <h4 className="text-sm font-medium text-white">{type.label}</h4>
                          <p className="text-xs text-gray-500 mt-1">{type.description}</p>
                          {isSelected && (
                            <div className={`mt-2 flex items-center gap-1 text-xs ${type.iconColor}`}>
                              <Check className="w-3 h-3" />
                              <span>Selected</span>
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Form Builder */}
            {activeTab === 'form' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-gray-400 text-sm mb-2 block">Headline</label>
                    <input
                      value={formData.formConfig.headline}
                      onChange={(e) => updateFormData('formConfig.headline', e.target.value)}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                    />
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm mb-2 block">Button Text</label>
                    <input
                      value={formData.formConfig.buttonText}
                      onChange={(e) => updateFormData('formConfig.buttonText', e.target.value)}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Subheadline</label>
                  <textarea
                    value={formData.formConfig.subheadline}
                    onChange={(e) => updateFormData('formConfig.subheadline', e.target.value)}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm h-16 resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                  />
                </div>

                {/* Lead Magnet specific */}
                {formData.type === 'lead_magnet' && (
                  <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-4 space-y-3">
                    <h4 className="text-sm font-medium text-white flex items-center gap-2">
                      <Download className="w-4 h-4 text-purple-400" />
                      Lead Magnet Download Settings
                    </h4>
                    <div>
                      <label className="text-gray-400 text-sm mb-2 block">Download URL</label>
                      <input
                        value={formData.formConfig.downloadUrl}
                        onChange={(e) => updateFormData('formConfig.downloadUrl', e.target.value)}
                        placeholder="https://your-file.pdf"
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={formData.submitBehavior.downloadTrigger}
                        onChange={(v) => updateFormData('submitBehavior.downloadTrigger', v)}
                      />
                      <label className="text-gray-300 text-sm">Auto-download on submit</label>
                    </div>
                    <p className="text-xs text-purple-300/70">
                      Users will receive immediate access to your downloadable content after form submission.
                    </p>
                  </div>
                )}

                {/* Calendar specific */}
                {formData.type === 'calendar_booking' && (
                  <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-xl p-4 space-y-3">
                    <h4 className="text-sm font-medium text-white flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-cyan-400" />
                      Calendar Booking Settings
                    </h4>
                    <div>
                      <label className="text-gray-400 text-sm mb-2 block">Calendar URL (Calendly, Cal.com, etc.)</label>
                      <input
                        value={formData.formConfig.calendarUrl}
                        onChange={(e) => updateFormData('formConfig.calendarUrl', e.target.value)}
                        placeholder="https://calendly.com/..."
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                      />
                    </div>
                    <p className="text-xs text-cyan-300/70">
                      After form submission, users will be redirected to this calendar link to book their slot.
                    </p>
                  </div>
                )}

                {/* SMS Opt-in specific */}
                {formData.type === 'sms_optin' && (
                  <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 space-y-3">
                    <h4 className="text-sm font-medium text-white flex items-center gap-2">
                      <Phone className="w-4 h-4 text-green-400" />
                      SMS Opt-in Settings
                    </h4>
                    <p className="text-xs text-green-300/70">
                      SMS consent is automatically required for this type. Make sure to configure your consent text in the form fields below.
                    </p>
                    <div className="flex items-center gap-2 p-2 bg-green-500/10 rounded-lg">
                      <Check className="w-4 h-4 text-green-400" />
                      <span className="text-xs text-green-300">SMS consent checkbox will be added automatically</span>
                    </div>
                  </div>
                )}

                {/* Replay Access specific */}
                {formData.type === 'replay_upsell' && (
                  <div className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-4 space-y-3">
                    <h4 className="text-sm font-medium text-white flex items-center gap-2">
                      <FileText className="w-4 h-4 text-orange-400" />
                      Replay Access Settings
                    </h4>
                    <div>
                      <label className="text-gray-400 text-sm mb-2 block">Replay URL</label>
                      <input
                        value={formData.formConfig.downloadUrl}
                        onChange={(e) => updateFormData('formConfig.downloadUrl', e.target.value)}
                        placeholder="https://your-replay-url.com/..."
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/50"
                      />
                    </div>
                    <p className="text-xs text-orange-300/70">
                      Users will receive this replay link after submitting their information.
                    </p>
                  </div>
                )}

                {/* Quiz Assessment specific */}
                {formData.type === 'quiz_assessment' && (
                  <div className="bg-pink-500/10 border border-pink-500/20 rounded-xl p-4 space-y-3">
                    <h4 className="text-sm font-medium text-white flex items-center gap-2">
                      <HelpCircle className="w-4 h-4 text-pink-400" />
                      Quiz Assessment Settings
                    </h4>
                    <p className="text-xs text-pink-300/70">
                      Collect lead information before or after presenting quiz questions. Perfect for assessments, surveys, and interactive content.
                    </p>
                    <div>
                      <label className="text-gray-400 text-sm mb-2 block">Results/Thank You URL (optional)</label>
                      <input
                        value={formData.submitBehavior.redirectUrl}
                        onChange={(e) => updateFormData('submitBehavior.redirectUrl', e.target.value)}
                        placeholder="https://your-results-page.com/..."
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-pink-500/50"
                      />
                    </div>
                  </div>
                )}

                {/* Form Fields */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-gray-400 text-sm">Form Fields</label>
                    <button
                      onClick={addField}
                      className="flex items-center gap-1 px-3 py-1.5 bg-white/5 border border-white/20 text-white text-sm rounded-lg hover:bg-white/10 transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Add Field
                    </button>
                  </div>
                  <div className="space-y-2">
                    {formData.formConfig.fields.map((field) => (
                      <div key={field.id} className="bg-white/5 rounded-lg p-3 flex items-start gap-3">
                        <GripVertical className="w-4 h-4 text-gray-500 mt-2 cursor-move" />
                        <div className="flex-1 grid grid-cols-4 gap-2">
                          <input
                            value={field.label}
                            onChange={(e) => updateField(field.id, { label: e.target.value, name: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                            placeholder="Label"
                            className="px-2 py-1.5 bg-white/5 border border-white/10 rounded text-white text-sm focus:outline-none"
                          />
                          <Select
                            value={field.type}
                            onChange={(v) => updateField(field.id, { type: v })}
                            options={FIELD_TYPES}
                          />
                          <input
                            value={field.placeholder || ''}
                            onChange={(e) => updateField(field.id, { placeholder: e.target.value })}
                            placeholder="Placeholder"
                            className="px-2 py-1.5 bg-white/5 border border-white/10 rounded text-white text-sm focus:outline-none"
                          />
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={field.required}
                              onChange={(v) => updateField(field.id, { required: v })}
                            />
                            <span className="text-xs text-gray-400">Required</span>
                          </div>
                        </div>
                        <button
                          onClick={() => removeField(field.id)}
                          className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Consent */}
                <div className="bg-white/5 rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-white">Consent Checkbox</h4>
                    <Switch
                      checked={formData.consent.enabled}
                      onChange={(v) => updateFormData('consent.enabled', v)}
                    />
                  </div>
                  {formData.consent.enabled && (
                    <>
                      <input
                        value={formData.consent.text}
                        onChange={(e) => updateFormData('consent.text', e.target.value)}
                        placeholder="I agree to receive marketing emails"
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none"
                      />
                      <input
                        value={formData.consent.privacyPolicyUrl}
                        onChange={(e) => updateFormData('consent.privacyPolicyUrl', e.target.value)}
                        placeholder="Privacy Policy URL"
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none"
                      />
                    </>
                  )}
                </div>

                {/* Success Behavior */}
                <div className="bg-white/5 rounded-xl p-4 space-y-3">
                  <h4 className="text-sm font-medium text-white">After Submission</h4>
                  <div>
                    <label className="text-gray-400 text-sm mb-2 block">Success Message</label>
                    <input
                      value={formData.submitBehavior.successMessage}
                      onChange={(e) => updateFormData('submitBehavior.successMessage', e.target.value)}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm mb-2 block">Redirect URL (optional)</label>
                    <input
                      value={formData.submitBehavior.redirectUrl}
                      onChange={(e) => updateFormData('submitBehavior.redirectUrl', e.target.value)}
                      placeholder="https://..."
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Branding */}
            {activeTab === 'branding' && (
              <div className="space-y-4">
                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Featured Image</label>
                  <p className="text-xs text-gray-500 mb-2">Upload an image to showcase your lead magnet. Recommended: 1200x600px</p>
                  <div className="flex items-start gap-3">
                    {formData.branding.imageUrl && (
                      <div className="w-32 h-24 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center p-2 overflow-hidden">
                        <img src={formData.branding.imageUrl} alt="Featured" className="max-w-full max-h-full object-cover rounded" />
                      </div>
                    )}
                    <label className="flex-1">
                      <div className="flex items-center justify-center gap-2 px-4 py-3 bg-white/5 hover:bg-white/10 border border-white/20 rounded-lg cursor-pointer transition-colors">
                        {uploading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            <span className="text-sm text-gray-300">Uploading...</span>
                          </>
                        ) : (
                          <>
                            <Image className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-300">{formData.branding.imageUrl ? 'Change Image' : 'Upload Image'}</span>
                          </>
                        )}
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'imageUrl')}
                        disabled={uploading}
                        className="hidden"
                      />
                    </label>
                    {formData.branding.imageUrl && (
                      <button
                        onClick={() => updateFormData('branding.imageUrl', '')}
                        className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Logo Image</label>
                  <p className="text-xs text-gray-500 mb-2">Optional logo to display on the form</p>
                  <div className="flex items-center gap-3">
                    {formData.branding.logoUrl && (
                      <div className="w-20 h-20 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center p-2 overflow-hidden">
                        <img src={formData.branding.logoUrl} alt="Logo" className="max-w-full max-h-full object-contain" />
                      </div>
                    )}
                    <label className="flex-1">
                      <div className="flex items-center justify-center gap-2 px-4 py-3 bg-white/5 hover:bg-white/10 border border-white/20 rounded-lg cursor-pointer transition-colors">
                        {uploading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            <span className="text-sm text-gray-300">Uploading...</span>
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-300">{formData.branding.logoUrl ? 'Change Logo' : 'Upload Logo'}</span>
                          </>
                        )}
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'logoUrl')}
                        disabled={uploading}
                        className="hidden"
                      />
                    </label>
                    {formData.branding.logoUrl && (
                      <button
                        onClick={() => updateFormData('branding.logoUrl', '')}
                        className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-gray-400 text-sm mb-2 block">Primary Color</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.branding.primaryColor}
                        onChange={(e) => updateFormData('branding.primaryColor', e.target.value)}
                        className="w-10 h-10 rounded cursor-pointer border-0"
                      />
                      <input
                        value={formData.branding.primaryColor}
                        onChange={(e) => updateFormData('branding.primaryColor', e.target.value)}
                        className="flex-1 px-2 bg-white/5 border border-white/10 rounded text-white text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm mb-2 block">Button Color</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.branding.buttonColor}
                        onChange={(e) => updateFormData('branding.buttonColor', e.target.value)}
                        className="w-10 h-10 rounded cursor-pointer border-0"
                      />
                      <input
                        value={formData.branding.buttonColor}
                        onChange={(e) => updateFormData('branding.buttonColor', e.target.value)}
                        className="flex-1 px-2 bg-white/5 border border-white/10 rounded text-white text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm mb-2 block">Background</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.branding.backgroundColor}
                        onChange={(e) => updateFormData('branding.backgroundColor', e.target.value)}
                        className="w-10 h-10 rounded cursor-pointer border-0"
                      />
                      <input
                        value={formData.branding.backgroundColor}
                        onChange={(e) => updateFormData('branding.backgroundColor', e.target.value)}
                        className="flex-1 px-2 bg-white/5 border border-white/10 rounded text-white text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Integration */}
            {activeTab === 'integration' && (
              <div className="space-y-4">
                <div className="bg-white/5 rounded-xl p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Zap className="w-5 h-5 text-amber-400" />
                      <h4 className="text-sm font-medium text-white">CRM Integration</h4>
                    </div>
                    <Switch
                      checked={formData.crmIntegration.enabled}
                      onChange={(v) => updateFormData('crmIntegration.enabled', v)}
                    />
                  </div>

                  {formData.crmIntegration.enabled && (
                    <>
                      <div>
                        <label className="text-gray-400 text-sm mb-2 block">Provider</label>
                        <Select
                          value={formData.crmIntegration.provider}
                          onChange={(v) => updateFormData('crmIntegration.provider', v)}
                          options={CRM_PROVIDERS}
                        />
                      </div>

                      {formData.crmIntegration.provider === 'webhook' && (
                        <div>
                          <label className="text-gray-400 text-sm mb-2 block">Webhook URL</label>
                          <input
                            value={formData.crmIntegration.webhookUrl}
                            onChange={(e) => updateFormData('crmIntegration.webhookUrl', e.target.value)}
                            placeholder="https://hooks.zapier.com/..."
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Submissions will be POSTed to this URL as JSON
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Preview */}
            {activeTab === 'preview' && (
              <div className="flex items-center justify-center min-h-[400px]" style={{ background: '#0a0a1a' }}>
                <div
                  className="w-full max-w-md rounded-2xl p-6 shadow-2xl"
                  style={{ backgroundColor: formData.branding.backgroundColor }}
                >
                  {formData.branding.imageUrl && (
                    <div className="mb-4 rounded-lg overflow-hidden">
                      <img src={formData.branding.imageUrl} alt="Featured" className="w-full h-48 object-cover" />
                    </div>
                  )}
                  {formData.branding.logoUrl && (
                    <div className="flex justify-center mb-4">
                      <img src={formData.branding.logoUrl} alt="Logo" className="h-12 object-contain" />
                    </div>
                  )}
                  <h2 className="text-xl font-bold text-white mb-2" style={{ color: formData.branding.primaryColor }}>
                    {formData.formConfig.headline || 'Your Headline'}
                  </h2>
                  {formData.formConfig.subheadline && (
                    <p className="text-gray-400 text-sm mb-4">{formData.formConfig.subheadline}</p>
                  )}
                  <div className="space-y-3">
                    {formData.formConfig.fields.map(field => (
                      <div key={field.id}>
                        <label className="block text-xs text-gray-400 mb-1">
                          {field.label} {field.required && <span className="text-red-400">*</span>}
                        </label>
                        <input
                          type={field.type}
                          placeholder={field.placeholder}
                          className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
                          disabled
                        />
                      </div>
                    ))}
                    {formData.consent.enabled && (
                      <label className="flex items-start gap-2 text-xs text-gray-400">
                        <input type="checkbox" className="mt-0.5" disabled />
                        <span>{formData.consent.text}</span>
                      </label>
                    )}
                    <button
                      className="w-full py-2.5 rounded-lg text-white font-medium text-sm"
                      style={{ backgroundColor: formData.branding.buttonColor }}
                      disabled
                    >
                      {formData.formConfig.buttonText}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-4 border-t border-white/10">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-white/5 border border-white/20 text-white rounded-lg hover:bg-white/10 transition-colors"
            >
              Cancel
            </button>
            {readOnly ? (onCopyToMyElements && (
              <button
                type="button"
                onClick={onCopyToMyElements}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-[#6a1fbf] to-[#00c853] rounded-lg text-white hover:opacity-90 transition-all"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                Copy to My Elements
              </button>
            )) : (
              <button
                onClick={handleSave}
                disabled={!isValid}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Check className="w-4 h-4" />
                {leadCapture ? 'Save Changes' : 'Create Form'}
              </button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}
